import { ExportJobStatusResponseDto, ExportJobStatusResponseDtoStatusEnum } from '@/api/generated';
import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import fileDownload from 'js-file-download';
import { useCallback, useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 2_000;
const STORAGE_KEY_PREFIX = 'fullCandidatesExportJobId:';

interface Params {
  adminUserId: string;
}

/** Days of the period, YYYY-MM-DD; either may be missing */
interface Period {
  from?: string;
  to?: string;
}

interface Return {
  dismiss: () => void;
  errorMessage: string | null;
  isStarting: boolean;
  progress: number;
  startExport: (period?: Period) => Promise<void>;
  uiState: UiState;
}

type UiState = 'completed' | 'failed' | 'idle' | 'pending' | 'running';

const storageKey = (adminUserId: string) => `${STORAGE_KEY_PREFIX}${adminUserId}`;

const readPersistedJobId = (adminUserId: string): string | null => {
  if (typeof window === 'undefined' || !adminUserId) return null;
  return window.localStorage.getItem(storageKey(adminUserId));
};

const persistJobId = (adminUserId: string, jobId: string | null): void => {
  if (typeof window === 'undefined' || !adminUserId) return;
  if (jobId) {
    window.localStorage.setItem(storageKey(adminUserId), jobId);
  } else {
    window.localStorage.removeItem(storageKey(adminUserId));
  }
};

const toUiState = (status?: ExportJobStatusResponseDtoStatusEnum): UiState => {
  switch (status) {
    case ExportJobStatusResponseDtoStatusEnum.Completed:
      return 'completed';
    case ExportJobStatusResponseDtoStatusEnum.Failed:
      return 'failed';
    case ExportJobStatusResponseDtoStatusEnum.Pending:
      return 'pending';
    case ExportJobStatusResponseDtoStatusEnum.Running:
      return 'running';
    default:
      return 'idle';
  }
};

const isActive = (status?: ExportJobStatusResponseDtoStatusEnum): boolean =>
  status === ExportJobStatusResponseDtoStatusEnum.Pending ||
  status === ExportJobStatusResponseDtoStatusEnum.Running;

export const useFullCandidatesExport = ({ adminUserId }: Params): Return => {
  const { adminCandidatesApi } = useApiClient();
  const { t } = useTranslationWithPrefix('views.admin.reports.fullExport');
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(() => readPersistedJobId(adminUserId));
  // A flag, not a text, so the message follows a language switch
  const [downloadFailed, setDownloadFailed] = useState(false);

  // Sync jobId state when adminUserId changes (e.g. login as different admin).
  // Intentional setState in effect — we sync from external store (localStorage) on prop change.
  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setJobId(readPersistedJobId(adminUserId));
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setDownloadFailed(false);
  }, [adminUserId]);

  const statusQuery = useQuery<ExportJobStatusResponseDto | null, AxiosError>({
    enabled: !!jobId && !!adminUserId,
    queryFn: async () => {
      if (!jobId) return null;
      try {
        const response = await adminCandidatesApi.getCandidatesFullExportStatus(adminUserId, jobId);
        return response.data;
      } catch (err) {
        // Job expired or unknown — clear and return to idle
        if (err instanceof AxiosError && err.response?.status === 404) {
          persistJobId(adminUserId, null);
          setJobId(null);
          return null;
        }
        throw err;
      }
    },
    queryKey: ['full-candidates-export-status', adminUserId, jobId],
    refetchInterval: query => (isActive(query.state.data?.status) ? POLL_INTERVAL_MS : false),
    refetchIntervalInBackground: true,
  });

  const startMutation = useMutation<string, AxiosError, Period | undefined>({
    mutationFn: async period => {
      const response = await adminCandidatesApi.startCandidatesFullExport(
        adminUserId,
        period?.from,
        period?.to,
      );
      return response.data.jobId;
    },
    onSuccess: newJobId => {
      persistJobId(adminUserId, newJobId);
      setJobId(newJobId);
      setDownloadFailed(false);
      void queryClient.invalidateQueries({
        queryKey: ['full-candidates-export-status', adminUserId],
      });
    },
  });

  const downloadMutation = useMutation<void, AxiosError, string>({
    mutationFn: async (completedJobId: string) => {
      const response = await adminCandidatesApi.downloadCandidatesFullExport(
        adminUserId,
        completedJobId,
        { responseType: 'blob' },
      );
      const headerName =
        response.headers['content-disposition']?.match(/attachment; filename=(.*)/)?.[1];
      const fileName = headerName ? decodeURIComponent(headerName) : 'candidates-full-export.xlsx';
      fileDownload(
        response.data,
        fileName,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
    },
    onError: () => {
      setDownloadFailed(true);
    },
    onSuccess: () => {
      persistJobId(adminUserId, null);
      setJobId(null);
      setDownloadFailed(false);
    },
  });

  // Auto-download when status flips to COMPLETED
  const completedJobId =
    statusQuery.data?.status === ExportJobStatusResponseDtoStatusEnum.Completed
      ? statusQuery.data.jobId
      : null;
  useEffect(() => {
    if (!completedJobId) return;
    if (downloadMutation.isPending || downloadMutation.isSuccess) return;
    downloadMutation.mutate(completedJobId);
  }, [completedJobId, downloadMutation]);

  const startExport = useCallback(
    async (period?: Period) => {
      setDownloadFailed(false);
      await startMutation.mutateAsync(period);
    },
    [startMutation],
  );

  const dismiss = useCallback(() => {
    persistJobId(adminUserId, null);
    setJobId(null);
    setDownloadFailed(false);
    downloadMutation.reset();
    startMutation.reset();
  }, [adminUserId, downloadMutation, startMutation]);

  const status = statusQuery.data?.status;
  const uiState: UiState = !jobId ? 'idle' : toUiState(status);
  const progress = statusQuery.data?.progress ?? 0;
  const errorMessage =
    (downloadFailed ? t('downloadFailed') : null) ??
    statusQuery.data?.errorMessage ??
    (startMutation.error ? t('startFailed') : null);

  return {
    dismiss,
    errorMessage,
    isStarting: startMutation.isPending,
    progress,
    startExport,
    uiState,
  };
};
